import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Konva from "konva";
import * as ReactKonva from "react-konva";
import Image from "image-js";

import {
  setSelectedAnnotations,
  stageHeightSelector,
  stagePositionSelector,
  stageScaleSelector,
  stageWidthSelector,
} from "store/annotator";

import { decodedAnnotationType, Shape } from "types";

import { hexToRGBA } from "utils/common/imageHelper";
import { colorOverlayROI } from "utils/common/imageHelper";
import { useImageOrigin } from "hooks";

type AnnotationProps = {
  annotation: decodedAnnotationType;
  imageShape: Shape;
  fillColor: string;
  selected?: boolean;
};

export const Annotation = ({
  annotation,
  imageShape,
  fillColor,
  selected,
}: AnnotationProps) => {
  const annotatorRef = useRef<Konva.Image | null>(null);

  const [imageWidth] = useState<number>(imageShape.width);
  const [imageHeight] = useState<number>(imageShape.height);
  const stageWidth = useSelector(stageWidthSelector);
  const stageHeight = useSelector(stageHeightSelector);
  const stageScale = useSelector(stageScaleSelector);
  const [imageMask, setImageMask] = useState<HTMLImageElement>();
  const imagePosition = useImageOrigin();
  const dispatch = useDispatch();

  useEffect(() => {
    const boxWidth = annotation.boundingBox[2] - annotation.boundingBox[0];
    const boxHeight = annotation.boundingBox[3] - annotation.boundingBox[1];
    if (!boxWidth || !boxHeight) return;
    if (Math.round(boxWidth) <= 0 || Math.round(boxHeight) <= 0) return;
    const color = hexToRGBA(fillColor, 0);
    setImageMask(
      colorOverlayROI(
        annotation.maskData,
        annotation.boundingBox,
        imageWidth,
        imageHeight,
        color,
        1
      )
    );
  }, [
    annotation.maskData,
    fillColor,
    annotation.boundingBox,
    imageWidth,
    imageHeight,
    imagePosition,
  ]);

  const onTransformEnd = () => {
    if (!selected) return;

    if (!imageWidth || !imageHeight) return;

    const node = annotatorRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    if (!scaleX || !scaleY) return;

    const maskData = annotation.maskData;
    const boundingBox = annotation.boundingBox;
    console.log(boundingBox);

    const roiWidth = boundingBox[2] - boundingBox[0];
    const roiHeight = boundingBox[3] - boundingBox[1];

    if (!roiWidth || !roiHeight) return;

    const roi = new Image(roiWidth, roiHeight, maskData, {
      components: 1,
      alpha: 0,
    });

    const resizedMaskROI = roi.resize({
      height: Math.round(roiHeight * scaleY),
      width: Math.round(roiWidth * scaleX),
      preserveAspectRatio: false,
    });
    const stageScaledX = Math.round(node.x()) - imagePosition.x;
    const stageScaleY = Math.round(node.y()) - imagePosition.y;

    const updatedAnnotation = {
      ...annotation,
      boundingBox: [
        stageScaledX,
        stageScaleY,
        stageScaledX + resizedMaskROI.width,
        stageScaleY + resizedMaskROI.height,
      ] as [number, number, number, number],
      maskData: Uint8Array.from(resizedMaskROI.data),
    };

    dispatch(
      setSelectedAnnotations({
        selectedAnnotations: [updatedAnnotation],
        workingAnnotation: updatedAnnotation,
      })
    );
  };

  return (
    <ReactKonva.Image
      ref={annotatorRef}
      id={annotation.id}
      image={imageMask}
      x={annotation.boundingBox[0] + imagePosition.x}
      y={annotation.boundingBox[1] + imagePosition.y}
      width={Math.round(annotation.boundingBox[2] - annotation.boundingBox[0])}
      height={Math.round(annotation.boundingBox[3] - annotation.boundingBox[1])}
      onTransformEnd={onTransformEnd}
      strokeWidth={100}
    />
  );
};
